import type {ConnectionOperation} from '@github-ui/optimistic-connection'
import {settleConfirmedOperation} from '@github-ui/optimistic-connection/settleConfirmedOperation'
import {useConnectionMutation} from '@github-ui/optimistic-connection/useConnectionMutation'
import {useQueryClient} from '@github-ui/react-query'
import type {AddRelatesToMutationMutation} from '@github-ui/server-persisted-queries/types/addRelatesToMutation'
import {reactFetchJSON} from '@github-ui/verified-fetch'
import {useCallback} from 'react'
import {ConnectionHandler, useRelayEnvironment} from 'react-relay'
import {commitLocalUpdate, type Disposable, type RecordSourceProxy} from 'relay-runtime'

import {issueViewerSecondaryViewQueryKey} from '../components/fetch-issue-viewer-secondary-view'
import {dependenciesPickerRelationshipsQueryKey} from '../components/sections/relations-section/fetch-dependencies-picker-relationships'
import {RELATES_TO_LIST_ALL_RELAY_CONNECTION} from '../components/sections/relations-section/LazyRelationshipsRelatesToListView'
import {RELATES_TO_CONNECTION_PATH} from '../components/sections/relations-section/relates-to-query-types'
import {escapeOptimisticTitleHTML} from '../utils/helpers'
import type {OptimisticDependencyIssue} from './add-blocked-by-mutation'

type AddRelatesToVariables = {
  issueId: string
  nameWithOwner: string
  issueNumber: number
}

type AddRelatesToOperation = Extract<ConnectionOperation, {type: 'addToConnection'}>

function toOptimisticOperation(relatedIssue: OptimisticDependencyIssue): AddRelatesToOperation {
  const [owner, name] = relatedIssue.repository.nameWithOwner.split('/')
  return {
    type: 'addToConnection',
    connectionPath: RELATES_TO_CONNECTION_PATH,
    anchor: {at: 'end'},
    counts: [{path: [...RELATES_TO_CONNECTION_PATH, 'totalCount'], delta: 1}],
    node: {
      ...relatedIssue,
      titleHTML: escapeOptimisticTitleHTML(relatedIssue.title),
      repository: {...relatedIssue.repository, name, owner: {login: owner}},
    },
  }
}

async function addRelatesToRequest({
  nameWithOwner,
  issueNumber,
  relatedIssueId,
}: AddRelatesToVariables & {relatedIssueId: string}): Promise<AddRelatesToMutationMutation> {
  const response = await reactFetchJSON(`/${nameWithOwner}/issues/${issueNumber}/relates_to.json`, {
    method: 'POST',
    body: {relatedIssueId},
  })
  const result = (await response.json()) as {
    data?: AddRelatesToMutationMutation | null
    errors?: ReadonlyArray<{message?: string}> | null
  }
  const error = result.errors?.find(item => item.message)?.message ?? result.data?.addRelatesTo?.errors[0]?.message
  if (!response.ok || error || !result.data?.addRelatesTo?.issue) {
    throw new Error(error ?? 'Failed to add related issue')
  }
  return result.data
}

type RelatesToMutationSide = NonNullable<NonNullable<AddRelatesToMutationMutation['addRelatesTo']>['issue']>

export function writeConfirmedRelatesTo(
  store: RecordSourceProxy,
  sides: ReadonlyArray<RelatesToMutationSide | null | undefined>,
): void {
  for (const side of sides) {
    if (!side) continue
    const issueRecord = store.get(side.id)
    const connection = issueRecord?.getLinkedRecord('relatesTo', {first: 3, ranked: true})
    if (!connection) continue

    const nodes = (side.topRelatesTo.nodes ?? []).flatMap(node => {
      if (!node) return []
      let nodeRecord = store.get(node.id)
      if (!nodeRecord) nodeRecord = store.create(node.id, 'Issue')
      nodeRecord.setValue(node.title, 'title')
      // eslint-disable-next-line @github-ui/github-monorepo/no-unsafe-titlehtml
      nodeRecord.setValue(node.titleHTML, 'titleHTML')
      nodeRecord.setValue(node.url, 'url')
      nodeRecord.setValue(node.number, 'number')
      nodeRecord.setValue(node.state, 'state')
      nodeRecord.setValue(node.stateReason, 'stateReason')

      let repositoryRecord = store.get(node.repository.id)
      if (!repositoryRecord) repositoryRecord = store.create(node.repository.id, 'Repository')
      repositoryRecord.setValue(node.repository.name, 'name')
      repositoryRecord.setValue(node.repository.nameWithOwner, 'nameWithOwner')

      let ownerRecord = store.get(node.repository.owner.id)
      if (!ownerRecord) ownerRecord = store.create(node.repository.owner.id, node.repository.owner.__typename)
      ownerRecord.setValue(node.repository.owner.login, 'login')
      repositoryRecord.setLinkedRecord(ownerRecord, 'owner')
      nodeRecord.setLinkedRecord(repositoryRecord, 'repository')
      return [nodeRecord]
    })
    connection.setLinkedRecords(nodes, 'nodes')
    connection.setValue(side.topRelatesTo.totalCount, 'totalCount')
    connection.getLinkedRecord('pageInfo')?.setValue(side.topRelatesTo.pageInfo.hasNextPage, 'hasNextPage')
  }
}

export function invalidateRelayRelatesToConnections(store: RecordSourceProxy, issueIds: string[]): void {
  for (const issueId of issueIds) {
    store.get(issueId)?.getLinkedRecord('relatesTo', {first: 100})?.invalidateRecord()
    store.get(ConnectionHandler.getConnectionID(issueId, RELATES_TO_LIST_ALL_RELAY_CONNECTION))?.invalidateRecord()
  }
}

export function useAddRelatesToMutation({issueId, nameWithOwner, issueNumber}: AddRelatesToVariables) {
  const environment = useRelayEnvironment()
  const queryClient = useQueryClient()
  const [owner, repo] = nameWithOwner.split('/')
  const secondaryQueryKey = issueViewerSecondaryViewQueryKey(owner ?? '', repo ?? '', issueNumber)
  const pickerQueryKey = dependenciesPickerRelationshipsQueryKey(nameWithOwner, issueNumber)

  const {mutateAsync} = useConnectionMutation<AddRelatesToMutationMutation, Error, Disposable>({
    ownerId: issueId,
    connectionPath: RELATES_TO_CONNECTION_PATH,
    mutationFn: operation =>
      addRelatesToRequest({
        nameWithOwner,
        issueNumber,
        issueId,
        relatedIssueId: (operation as AddRelatesToOperation).node.id,
      }),
    onMutate: ({operation}) => {
      const relatedIssue = (operation as AddRelatesToOperation).node as unknown as OptimisticDependencyIssue
      return environment.applyUpdate({
        storeUpdater: store => optimisticAddRelatesTo(store, {issueId}, relatedIssue),
      })
    },
    onSuccess: async (data, variables, revertRelayStore) => {
      const issue = data.addRelatesTo?.issue
      try {
        if (issue) {
          await settleConfirmedOperation({
            queryClient,
            targets: [{queryKey: secondaryQueryKey}],
            ownerId: issueId,
            operation: {type: 'setFields', id: issue.id, fields: {topRelatesTo: issue.topRelatesTo}},
          })
        }
        await queryClient.invalidateQueries({queryKey: pickerQueryKey})
        commitLocalUpdate(environment, store => {
          writeConfirmedRelatesTo(store, [data.addRelatesTo?.issue, data.addRelatesTo?.relatedIssue])
          invalidateRelayRelatesToConnections(store, [issueId, (variables.operation as AddRelatesToOperation).node.id])
        })
      } finally {
        revertRelayStore?.dispose()
      }
    },
    onError: (_error, _variables, revertRelayStore) => revertRelayStore?.dispose(),
  })

  return useCallback(
    (relatedIssue: OptimisticDependencyIssue) => mutateAsync({operation: toOptimisticOperation(relatedIssue)}),
    [mutateAsync],
  )
}

function optimisticAddRelatesTo(
  store: RecordSourceProxy,
  input: {issueId: string},
  optimisticIssue: OptimisticDependencyIssue,
) {
  // Only the viewer's issue is reliably in the store; the server response reconciles the symmetric reverse edge.
  const issueRecord = store.get(input.issueId)
  if (!issueRecord) return

  let relatedRecord = store.get(optimisticIssue.id)
  if (!relatedRecord) {
    relatedRecord = store.create(optimisticIssue.id, 'Issue')
  }

  relatedRecord.setValue(optimisticIssue.title, 'title')
  // Escape the plaintext picker title; the server response overwrites with real HTML.
  relatedRecord.setValue(escapeOptimisticTitleHTML(optimisticIssue.title), 'titleHTML')
  relatedRecord.setValue(optimisticIssue.url, 'url')
  relatedRecord.setValue(optimisticIssue.number, 'number')
  relatedRecord.setValue(optimisticIssue.state, 'state')
  relatedRecord.setValue(optimisticIssue.stateReason ?? null, 'stateReason')

  let repoRecord = store.get(optimisticIssue.repository.id)
  if (!repoRecord) {
    repoRecord = store.create(optimisticIssue.repository.id, 'Repository')
  }
  repoRecord.setValue(optimisticIssue.repository.nameWithOwner, 'nameWithOwner')
  const [owner, name] = optimisticIssue.repository.nameWithOwner.split('/')
  repoRecord.setValue(name, 'name')

  let ownerRecord = repoRecord.getLinkedRecord('owner')
  if (!ownerRecord) {
    ownerRecord = store.create(`${optimisticIssue.repository.id}:owner:optimistic`, 'Organization')
    repoRecord.setLinkedRecord(ownerRecord, 'owner')
  }
  ownerRecord.setValue(owner, 'login')

  relatedRecord.setLinkedRecord(repoRecord, 'repository')

  const connection = issueRecord.getLinkedRecord('relatesTo', {first: 3, ranked: true})
  if (connection) {
    const existingNodes = connection.getLinkedRecords('nodes') || []
    if (!existingNodes.some(node => node?.getDataID() === optimisticIssue.id)) {
      connection.setLinkedRecords([...existingNodes, relatedRecord], 'nodes')
      // Keep the badge total in sync with the appended node; the server response reconciles the true total.
      const currentTotal = (connection.getValue('totalCount') as number) ?? 0
      connection.setValue(currentTotal + 1, 'totalCount')
    }
  }
}
